import { formatResumeBuilderText, getResumeBuilderContent } from "@/i18n/resumeBuilderContent";

export const RESUME_TEMPLATE_OPTIONS = [
  { id: 0, name: "Classic Professional", accent: "#1e3a5f", layout: "border" },
  { id: 1, name: "Modern Professional", accent: "#4f46e5", layout: "sidebar" },
  { id: 2, name: "Executive", accent: "#111827", layout: "rule" },
  { id: 3, name: "Technology & Data", accent: "#0f766e", layout: "blocks" },
  { id: 4, name: "Clean Minimal", accent: "#475569", layout: "minimal" },
  { id: 5, name: "Corporate", accent: "#1d4ed8", layout: "banner" },
  { id: 6, name: "Academic & Technical", accent: "#7c2d12", layout: "academic" },
  { id: 7, name: "International CV", accent: "#6d28d9", layout: "international" },
];

export const EMPTY_STRUCTURED_RESUME = {
  full_name: "",
  title: "",
  headline: "",
  email: "",
  phone: "",
  address: "",
  location: "",
  summary: "",
  objective: "",
  links: [],
  skills: [],
  experience: [],
  education: [],
  projects: [],
  certifications: [],
  accomplishments: [],
  leadership: [],
  memberships: [],
  other_sections: [],
};

export const EMPTY_CAREER_PROFILE = {
  schema_version: 1,
  professional_summary: "",
  professional_background: [],
  additional_responsibilities: [],
  accomplishments: [],
  projects: [],
  skills_tools: [],
  industries: [],
  leadership: [],
  certifications: [],
  education_details: [],
  github_portfolio_links: [],
  publications: [],
  awards: [],
  languages: [],
  clearances: [],
  other_verified_evidence: [],
  extra_fields: {},
};

export const RESUME_MODE_OPTIONS = [
  {
    value: "base",
    title: "Base Resume",
    description: "Use my finalized résumé without job-specific AI changes.",
  },
  {
    value: "smart",
    title: "Smart Tailor — Pro",
    description: "Conservatively tailor relevant résumé sections to each job.",
  },
  {
    value: "deep",
    title: "Deep Tailor — Pro",
    description: "Use my verified Career Profile for deeper job-specific tailoring.",
  },
];

export const RESUME_BUILDER_STEPS = [
  { key: "template", label: "Template" },
  { key: "edit", label: "Base Resume" },
  { key: "mode", label: "Resume Mode" },
  { key: "career-profile", label: "Career Profile" },
  { key: "review", label: "Review" },
];

export function templateForId(id) {
  return RESUME_TEMPLATE_OPTIONS.find((template) => template.id === id) || RESUME_TEMPLATE_OPTIONS[0];
}

export function localizedTemplateOptions(content = getResumeBuilderContent("en")) {
  return RESUME_TEMPLATE_OPTIONS.map((template, index) => ({
    ...template,
    name: content.presentation.templates[index] || template.name,
  }));
}

export function localizedTemplateForId(id, content = getResumeBuilderContent("en")) {
  return localizedTemplateOptions(content).find((template) => template.id === id)
    || localizedTemplateOptions(content)[0];
}

export function localizedResumeModeOptions(content = getResumeBuilderContent("en")) {
  return ["base", "smart", "deep"].map((value) => ({
    value,
    title: content.presentation.modes[value].titleWithPlan,
    description: content.presentation.modes[value].description,
  }));
}

export function localizedResumeBuilderSteps(content = getResumeBuilderContent("en")) {
  return RESUME_BUILDER_STEPS.map((step) => ({
    ...step,
    label: content.presentation.steps[step.key],
  }));
}

export function resumeModeLabel(mode, includePlan = true, content = getResumeBuilderContent("en")) {
  const normalized = mode === "deep" || mode === "smart" ? mode : "base";
  return includePlan
    ? content.presentation.modes[normalized].titleWithPlan
    : content.presentation.modes[normalized].title;
}

export function guidedResumeHref(slot, step) {
  const parsedSlot = Number(slot);
  if (!Number.isInteger(parsedSlot) || parsedSlot < 1 || parsedSlot > 5) return "/onboarding?tab=resumeProfiles";
  return `/resume-builder/${parsedSlot}/${step}`;
}

export function stepAfterMode(slot, mode) {
  return guidedResumeHref(slot, mode === "base" ? "review" : "career-profile");
}

export function deepReadinessMessage(readiness, content = getResumeBuilderContent("en")) {
  const copy = content.presentation.readiness;
  if (readiness?.deep_ready) return copy.ready;
  const missing = Array.isArray(readiness?.missing_sections) ? readiness.missing_sections : [];
  const labels = missing.map((item) => {
    if (item === "professional_background") return copy.professionalBackground;
    if (item === "verified_evidence") return copy.verifiedEvidence;
    return String(item || "").replaceAll("_", " ");
  }).filter(Boolean);
  return labels.length
    ? formatResumeBuilderText(copy.missingPrefix, { items: labels.join(copy.joinWord) })
    : copy.moreEvidence;
}

export async function resumeBuilderApi(url, options) {
  const response = await fetch(url, { cache: "no-store", ...options });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error?.message || body?.error || `Request failed (${response.status})`);
    error.code = body?.error?.code;
    throw error;
  }
  return body;
}
